import { groupBy, map } from 'lodash'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { Time } from '@/types/time'
import { timeWithoutSeconds } from '@/utils/time-without-seconds'
import { requiredString } from '@/utils/zod'

const responseSchema = z.object({
  start_hour: requiredString(),
  slot_duration: requiredString(),
  break_duration: requiredString(),
  num_of_slots: z.number(),
  custom_breaks: z.array(
    z.object({
      slot: z.number(),
      duration: requiredString()
    })
  ),
  should_display_regenerated_info: z.boolean()
})

export type TimetableSettingsCustomBreak = {
  slots: number[]
  duration: Time
}

export type TimetableSettings = {
  startHour: Time
  slotDuration: Time
  breakDuration: Time
  numOfSlots: number
  customBreaks: TimetableSettingsCustomBreak[]
  shouldDisplayRegeneratedInfo: boolean
}

const parseResponse = (
  response: z.infer<typeof responseSchema>
): TimetableSettings => ({
  startHour: timeWithoutSeconds(response.start_hour),
  slotDuration: timeWithoutSeconds(response.slot_duration),
  breakDuration: timeWithoutSeconds(response.break_duration),
  numOfSlots: response.num_of_slots,
  // Backend doesn't return custom breaks in a grouped format
  customBreaks: map(
    groupBy(response.custom_breaks, 'duration'),
    (customBreaks, duration) => ({
      duration: timeWithoutSeconds(duration),
      slots: customBreaks.map(customBreak => customBreak.slot)
    })
  ),
  shouldDisplayRegeneratedInfo: response.should_display_regenerated_info
})

export const timetableSettingsQueryOptions = {
  queryKey: ['panelTimetableSettingsRetrieve'],
  staleTime: 60 * 1000,
  queryFn: async () => {
    const response = await httpService.panel.panelTimetableSettingsRetrieve({
      fetchKeys: {
        start_hour: true,
        slot_duration: true,
        break_duration: true,
        num_of_slots: true,
        custom_breaks: true,
        should_display_regenerated_info: true
      }
    })

    return parseResponse(responseSchema.parse(response))
  }
}
